import { useEffect, useRef, useState } from 'react';

type ResizeCallback = (width: number, height: number) => void;

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  const timeoutId = useRef<number | null>(null);
  const animationFrameId = useRef<number | null>(null);
  const callbacks = useRef<Map<string, ResizeCallback>>(new Map());
  const registerCallback = (id: string, callback: ResizeCallback) => callbacks.current.set(id, callback);
  const removeCallbacks = (...ids: string[]) => ids.forEach((id) => callbacks.current.delete(id));
  const clearCallbacks = () => callbacks.current.clear();
  const triggerCallbacks = () => callbacks.current.forEach((callback) => windowSize && callback(windowSize.width, windowSize.height));

  useEffect(() => {
    if (!window) {
      return;
    }

    setWindowSize({ width: windowSize.width, height: windowSize.height });

    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
      callbacks.current.forEach((callback) => callback(windowSize.width, windowSize.height));
    };

    handleResize();

    const debouncedHandleResize = () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }

      timeoutId.current = window.setTimeout(() => {
        if (animationFrameId.current !== null) {
          cancelAnimationFrame(animationFrameId.current);
        }
        animationFrameId.current = requestAnimationFrame(handleResize);
      }, 20);
    };

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      if (timeoutId.current !== null) {
        clearTimeout(timeoutId.current);
      }
      if (animationFrameId.current !== null) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [callbacks, windowSize.height, windowSize.width]);

  return { windowSize, registerCallback, removeCallbacks, clearCallbacks, triggerCallbacks };
};

export default useWindowSize;
